import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Activity, AlertCircle, ArrowRight, Medal, Timer, Trophy } from 'lucide-react'
import { DataTable } from '@/components/shared/data-table'
import { SectionCard } from '@/components/shared/section-card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { formatNumber } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useStepsLeaderboard } from '@/hooks/useDashboardAggregates'
import { useLiveLeaderboard } from '@/hooks/useLeaderboard'

const RANGE_OPTIONS = [
  { label: '7 Days', days: 7 },
  { label: '14 Days', days: 14 },
  { label: '30 Days', days: 30 },
] as const

export function LeaderboardPage() {
  const [days, setDays] = useState<number>(7)
  const { data, loading, error } = useStepsLeaderboard(days, 20)
  const { data: liveLeaderboard } = useLiveLeaderboard()

  const podium = data.slice(0, 3)
  const rest = data.slice(3)

  return (
    <div className="space-y-6">
      {/* Header banner */}
      <div className="rounded-[36px] border border-stone-800 bg-[linear-gradient(135deg,#1c1c1c_0%,#0a0a0a_100%)] p-8 text-white shadow-2xl">
        <div className="inline-flex rounded-full border border-amber-500/20 bg-amber-500/10 px-4 py-1 text-xs uppercase tracking-[0.24em] text-amber-400">
          Leaderboard
        </div>
        <h2 className="mt-5 text-4xl font-semibold leading-tight text-stone-100">User Rankings</h2>
        <p className="mt-4 max-w-2xl text-base leading-7 text-stone-400">
          Ranking dihitung langsung dari data sensor user — workouts dan langkah kaki yang tersinkron ke Firestore.
        </p>

        {/* Summary cards */}
        <div className="mt-8 flex flex-wrap gap-4">
          <div className="rounded-2xl border border-stone-700 bg-stone-900/60 px-6 py-4 min-w-[180px]">
            <div className="flex items-center gap-2 text-orange-400 mb-1">
              <Timer className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide">Top Workouts</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {liveLeaderboard?.workoutsRanking[0]?.displayName ?? '—'}
            </p>
            <p className="text-xs text-stone-500 mt-1">
              {liveLeaderboard?.workoutsRanking[0]
                ? `${formatNumber(liveLeaderboard.workoutsRanking[0].workouts ?? 0)} sessions`
                : 'Memuat…'}
            </p>
          </div>
          <div className="rounded-2xl border border-stone-700 bg-stone-900/60 px-6 py-4 min-w-[180px]">
            <div className="flex items-center gap-2 text-emerald-400 mb-1">
              <Activity className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide">Most Steps ({days}d)</span>
            </div>
            <p className="text-2xl font-bold text-white">{data?.[0]?.displayName ?? '—'}</p>
            <p className="text-xs text-stone-500 mt-1">
              {data?.[0] ? `${formatNumber(data[0].totalSteps)} steps` : 'Memuat…'}
            </p>
          </div>
        </div>
      </div>

      {/* Range selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">Steps Leaderboard</h3>
        <div className="inline-flex rounded-full border border-stone-800 bg-stone-900/50 p-1">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              onClick={() => setDays(opt.days)}
              className={cn(
                'px-4 py-1.5 text-xs font-semibold transition rounded-full',
                days === opt.days ? 'bg-orange-500 text-white' : 'text-stone-400 hover:text-white',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-3xl border border-rose-500/20 bg-rose-500/5 p-6 flex items-start gap-3">
          <AlertCircle className="h-6 w-6 text-rose-500 shrink-0" />
          <div>
            <p className="font-semibold text-white">Aggregation failed</p>
            <p className="mt-1 text-sm text-stone-400">
              {error.message}. Verify Firestore allows{' '}
              <code className="rounded bg-stone-800 px-1.5 py-0.5 text-xs">collectionGroup('health')</code>{' '}
              queries (composite index on `date` may be required).
            </p>
          </div>
        </div>
      )}

      {/* Podium */}
      {podium.length > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          {podium.map((entry) => {
            const tone =
              entry.rank === 1
                ? { ring: 'ring-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-400', icon: Trophy }
                : entry.rank === 2
                ? { ring: 'ring-stone-400', bg: 'bg-stone-500/10', text: 'text-stone-300', icon: Medal }
                : { ring: 'ring-orange-700', bg: 'bg-orange-700/10', text: 'text-orange-400', icon: Medal }
            return (
              <div
                key={entry.uid}
                className={cn(
                  'rounded-3xl border border-stone-800 bg-[#1a1a1a] p-6 ring-2',
                  tone.ring,
                )}
              >
                <div className="flex items-center justify-between">
                  <div className={cn('rounded-full p-2', tone.bg)}>
                    <tone.icon className={cn('h-5 w-5', tone.text)} />
                  </div>
                  <span className={cn('text-2xl font-bold', tone.text)}>#{entry.rank}</span>
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <Avatar className="h-12 w-12 border border-stone-700">
                    <AvatarImage src={entry.photoUrl ?? undefined} />
                    <AvatarFallback className="bg-stone-800 text-stone-400">
                      {entry.displayName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-white">{entry.displayName}</p>
                    <p className="truncate text-xs text-stone-500">{entry.email ?? entry.uid}</p>
                  </div>
                </div>
                <div className="mt-5 space-y-1">
                  <p className="text-3xl font-bold text-white">{formatNumber(entry.totalSteps)}</p>
                  <p className="text-xs text-stone-500">
                    total steps · {entry.daysActive} active days · avg {formatNumber(entry.avgSteps)}/day
                  </p>
                </div>
                <Link
                  to={`/users/${entry.uid}`}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-orange-500/10 px-3 py-2 text-xs font-semibold text-orange-400 transition hover:bg-orange-500/20"
                >
                  <Activity className="h-3.5 w-3.5" />
                  View Metrics
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            )
          })}
        </div>
      )}

      {/* Steps full ranking + Workouts ranking side by side */}
      <div className="grid gap-6 xl:grid-cols-2">
        <SectionCard
          title={`Steps Ranking — Last ${days} Days`}
          description="Top 20 users by aggregated step count."
        >
          <DataTable
            rows={rest}
            pageSize={10}
            columns={[
              {
                header: 'Rank',
                render: (row) => (
                  <div className="flex items-center gap-2 font-semibold text-white">
                    <Medal className="h-4 w-4 text-stone-500" /> #{row.rank}
                  </div>
                ),
              },
              {
                header: 'User',
                render: (row) => (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9 border border-stone-700">
                      <AvatarImage src={row.photoUrl ?? undefined} />
                      <AvatarFallback className="bg-stone-800 text-[10px] text-stone-400">
                        {row.displayName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-white">{row.displayName}</p>
                      <p className="truncate text-xs text-stone-500">{row.email ?? row.uid}</p>
                    </div>
                  </div>
                ),
              },
              {
                header: 'Total Steps',
                render: (row) => (
                  <span className="font-bold text-orange-400">{formatNumber(row.totalSteps)}</span>
                ),
              },
              {
                header: 'Days Active',
                render: (row) => `${row.daysActive} / ${days}`,
              },
              {
                header: 'Action',
                render: (row) => (
                  <Link
                    to={`/users/${row.uid}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500/10 px-2.5 py-1.5 text-xs font-semibold text-orange-400 transition hover:bg-orange-500/20"
                  >
                    View
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                ),
              },
            ]}
            emptyTitle={loading ? 'Computing…' : 'No data'}
            emptyDescription={
              loading
                ? 'Reading and aggregating step data from all users.'
                : 'No users have synced step data in this period yet.'
            }
          />
        </SectionCard>

        <SectionCard
          title="Workouts Ranking (All-Time)"
          description="Total workout sessions ever synced per user."
        >
          <DataTable
            rows={liveLeaderboard?.workoutsRanking ?? []}
            pageSize={10}
            columns={[
              {
                header: 'Rank',
                render: (row) => (
                  <div className="flex items-center gap-2 font-semibold text-white">
                    <Medal className="h-4 w-4 text-stone-500" /> #{row.rank}
                  </div>
                ),
              },
              {
                header: 'User',
                render: (row) => (
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-white">{row.displayName}</p>
                    <p className="truncate text-xs text-stone-500">{row.uid.slice(0, 12)}…</p>
                  </div>
                ),
              },
              {
                header: 'Workouts',
                render: (row) => (
                  <span className="font-bold text-purple-400 tabular-nums">
                    {formatNumber(row.workouts ?? 0)}
                  </span>
                ),
              },
              {
                header: 'Action',
                render: (row) => (
                  <Link
                    to={`/users/${row.uid}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500/10 px-2.5 py-1.5 text-xs font-semibold text-orange-400 transition hover:bg-orange-500/20"
                  >
                    View
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                ),
              },
            ]}
            emptyTitle="Belum ada workout"
            emptyDescription="Data muncul saat user melakukan sinkronisasi sesi workout dari smartwatch."
          />
        </SectionCard>
      </div>
    </div>
  )
}
